import React, { useState, useRef, useEffect } from "react";
import { MapPin, X } from "lucide-react";

const inputCls = "w-full h-12 rounded-xl border border-[#E2E8F0] bg-white pl-9 pr-8 text-sm font-semibold text-[#0B2038] placeholder:text-[#94A3B8] placeholder:font-medium focus:outline-none focus:ring-2 focus:ring-[#009E9E]/40 focus:border-[#009E9E] transition-colors";

export const DestinationInput = ({ dests, value, onChange, testid, placeholder }) => {
  const selected = dests.find((d) => d.code === value);
  const [query, setQuery] = useState(selected ? selected.city : "");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    const s = dests.find((d) => d.code === value);
    if (s) setQuery(s.city);
  }, [value, dests]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const q = query.trim().toLowerCase();
  const matches = (q && (!selected || selected.city.toLowerCase() !== q)
    ? dests.filter((d) => d.city.toLowerCase().includes(q) || d.code.toLowerCase().includes(q) || (d.airport || "").toLowerCase().includes(q))
    : dests).slice(0, 8);

  const pick = (d) => { onChange(d.code); setQuery(d.city); setOpen(false); };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, matches.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && matches[active]) { e.preventDefault(); pick(matches[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative" ref={boxRef}>
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#009E9E] pointer-events-none" />
      <input data-testid={testid} value={query} placeholder={placeholder}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => { setOpen(true); setActive(0); }}
        onKeyDown={onKeyDown}
        autoComplete="off" className={inputCls} />
      {query && (
        <button type="button" data-testid={`${testid}-clear`} onClick={() => { setQuery(""); onChange(""); setOpen(true); }}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-[#F8FAFC] flex items-center justify-center">
          <X className="w-3.5 h-3.5 text-[#94A3B8]" />
        </button>
      )}
      {open && (
        <ul data-testid={`${testid}-suggestions`}
          className="absolute z-[620] left-0 right-0 mt-2 bg-white border border-[#E2E8F0] rounded-xl shadow-[0_12px_36px_rgba(11,32,56,0.14)] overflow-hidden max-h-72 overflow-y-auto">
          {matches.length === 0 && <li className="px-4 py-3 text-sm text-[#475569]">No matching destination</li>}
          {matches.map((d, i) => (
            <li key={d.code}>
              <button type="button" data-testid={`${testid}-option-${d.code}`} onMouseEnter={() => setActive(i)}
                onClick={() => pick(d)}
                className={`w-full text-start px-3.5 py-2.5 flex items-center gap-3 transition-colors ${i === active ? "bg-[#F8FAFC]" : "bg-white"}`}>
                <MapPin className="w-4 h-4 text-[#009E9E] shrink-0" />
                <span className="min-w-0">
                  <span className="block text-sm font-bold text-[#0B2038] truncate">{d.city}</span>
                  <span className="block text-[11px] text-[#475569]">{d.country || "Türkiye"} · {d.airport || d.code}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
